import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@/ui';

import { MentoCalDatePicker } from './DatePicker';
import { MentoCalPageShell } from './PageShell';
import { MentoCalTimeSlotButton } from './TimeSlotButton';
import { CoachHeader, DetailRow, SessionCardShell } from './sessionCard';
import {
  formatSessionRange,
  formatSessionTime,
  isSessionStarted,
  isWithinRescheduleWindow,
} from './sessionTiming';

import type {
  BrowserSchedulingContext,
  DecodedBrowserAppointment,
} from '@/features/appointments/types';
import type { Session } from '@/types';
import type { FC, ReactNode } from 'react';

import { SeriesCoachConflictNote } from '@/features/appointments/SeriesCoachConflictNote';
import {
  APPOINTMENT_RETRY_BUTTON_LABEL,
  appointmentPendingMessage,
  appointmentRejectionMessage,
  appointmentRestoredSubmittingMessage,
} from '@/features/appointments/appointmentStatusCopy';
import { PREVIOUS_EXACT_APPOINTMENT_CONTRACT } from '@/features/appointments/browserCapability';
import {
  futureSeriesRescheduleCommand,
  type FutureSeriesRescheduleCommand,
} from '@/features/appointments/futureSeriesRescheduleCommand';
import { slotCoachConflictStarts } from '@/features/appointments/schedulingContextAdapter';
import {
  appointmentSeriesCommandFields,
  appointmentUpgradeGuardSpent,
  canonicalAppointmentCommand,
  useAppointmentLifecycleGuard,
} from '@/features/appointments/useAppointmentLifecycleGuard';
import { api } from '@/store/api';
import {
  getAppointmentBrowserCapability,
  shouldReconcileAppointmentWrite,
  trustedAppointmentDeclineMessage,
  useGetAppointmentOperationQuery,
  useGetAppointmentSchedulingContextQuery,
  useLazyGetAppointmentQuery,
  useRescheduleAppointmentMutation,
} from '@/store/api/appointmentApi';
import {
  useGetSessionRescheduleOptionsQuery,
  useRescheduleSessionMutation,
} from '@/store/api/sessionApi';
import { useGetCurrentUserQuery } from '@/store/api/userApi';
import { useAppDispatch } from '@/store/hooks';
import {
  clearRescheduleMutationIntent,
  getRescheduleMutationIntent,
  getStoredRescheduleMutationIntent,
  isNonRetryableClientError,
  rescheduleMutationFingerprint,
} from '@/utils/rescheduleMutation';

interface MemberRescheduleSessionProps {
  session: Session;
  close: () => void;
  mode?: 'appointment' | 'futureSeries';
  onRescheduled?: () => void;
}

interface AppointmentRescheduleBaseline {
  appointmentId: string;
  expectedKind: 'chemistry' | 'coaching';
  expectedSeriesId: string | null;
  expectedSeriesMutationVersion: number | undefined;
  expectedVersion: number;
  startAt: string;
}

const PENDING_PRESENTATION_DELAY_MS = 60_000;
const WITHIN_48_HOURS_ACKNOWLEDGEMENT_ERROR =
  'confirm the session is within 48 hours before rescheduling';
const WITHIN_48_HOURS_ACKNOWLEDGEMENT_ERROR_CODE =
  'within_48_hours_confirmation_required';

const isWithin48HoursAcknowledgementError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const response = error as { data?: unknown; status?: unknown };
  if (
    response.status !== 422 ||
    !response.data ||
    typeof response.data !== 'object'
  )
    return false;

  const data = response.data as { code?: unknown; error?: unknown };
  return (
    data.code === WITHIN_48_HOURS_ACKNOWLEDGEMENT_ERROR_CODE ||
    data.error === WITHIN_48_HOURS_ACKNOWLEDGEMENT_ERROR
  );
};

const retainedAppointmentRescheduleBaseline = (
  bytes: string,
  expectedKind: AppointmentRescheduleBaseline['expectedKind'],
): AppointmentRescheduleBaseline | null => {
  try {
    const command = JSON.parse(bytes) as Partial<{
      appointmentId: unknown;
      expectedSeriesMutationVersion: unknown;
      expectedVersion: unknown;
      scope: unknown;
      seriesId: unknown;
      startAt: unknown;
    }>;
    const validSeriesFence =
      (command.seriesId === null &&
        command.expectedSeriesMutationVersion === null) ||
      (typeof command.seriesId === 'string' &&
        command.seriesId.length > 0 &&
        Number.isInteger(command.expectedSeriesMutationVersion) &&
        (command.expectedSeriesMutationVersion as number) > 0);
    if (
      command.scope !== 'appointment' ||
      typeof command.appointmentId !== 'string' ||
      !Number.isInteger(command.expectedVersion) ||
      (command.expectedVersion as number) <= 0 ||
      typeof command.startAt !== 'string' ||
      !validSeriesFence
    ) {
      return null;
    }
    return {
      appointmentId: command.appointmentId,
      expectedKind,
      expectedSeriesId: command.seriesId as string | null,
      expectedSeriesMutationVersion:
        command.seriesId === null
          ? 0
          : (command.expectedSeriesMutationVersion as number),
      expectedVersion: command.expectedVersion as number,
      startAt: command.startAt,
    };
  } catch {
    return null;
  }
};

// MEN-5652: the same bound the scheduling assistant's note has.
const MEMBER_RESCHEDULE_NOTE_MAX_LENGTH = 1000;

// MEN-5652: the note a retained command carries, so a remounted form shows it again and an
// unchanged retry rebuilds the same bytes. Null when the bytes are not this appointment's.
const retainedRescheduleNote = (
  bytes: string | undefined,
  appointmentId: string | null | undefined,
): string | null => {
  if (!bytes || !appointmentId) return null;
  try {
    const command = JSON.parse(bytes) as {
      appointmentId?: unknown;
      reason?: unknown;
    };
    return command.appointmentId === appointmentId &&
      typeof command.reason === 'string'
      ? command.reason
      : null;
  } catch {
    return null;
  }
};

const getViewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

// Human-friendly short timezone label (e.g. "PST") for display, while the
// IANA id (e.g. "America/Los_Angeles") is still used for API calls.
const shortTimezoneLabel = (timeZone: string, date: string) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date(date));
    return (
      parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone
    );
  } catch {
    return timeZone;
  }
};

const localDate = (value: string) => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatSlotTime = (value: string) =>
  new Date(value).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

const formatSeriesCadence = (
  cadenceWeeks: number,
  startsAt: string,
  timezone: string,
) => {
  const date = new Date(startsAt);
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
  }).format(date);
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  }).format(date);
  return `Every ${cadenceWeeks} weeks on ${weekday} at ${time}`;
};

const isRescheduleIntentBound = (
  intent: NonNullable<ReturnType<typeof getStoredRescheduleMutationIntent>>,
  session: Session,
) => {
  const expectedFingerprint = rescheduleMutationFingerprint([
    'member-chemistry-or-biweekly',
    session.userId,
    session.id,
    intent.originalStartsAt,
    intent.selectedStartsAt,
  ]);
  const scheduleMatches = [
    intent.originalStartsAt,
    intent.selectedStartsAt,
  ].includes(session.startedAt);
  return intent.fingerprint === expectedFingerprint && scheduleMatches;
};

const getBoundRescheduleIntent = (intentKey: string, session: Session) => {
  const intent = getStoredRescheduleMutationIntent(intentKey);
  if (!intent) return null;
  if (isRescheduleIntentBound(intent, session)) return intent;

  clearRescheduleMutationIntent(intentKey);
  return null;
};

// MEN-4687: For native appointments, `session.coachId` is populated from
// `expected_identities.coach.person_id` (Moments UUID) — the browser projection
// intentionally omits Rails ids. But the Rails-facing scheduling-context
// endpoint expects a Rails `User.id`, so passing the person_id there returns
// 404. Look up the Rails coach.id from the current user's coaches by matching
// display name; fall back to the sole coach if only one is assigned, and fail
// closed when no unique identity can be established.
const resolveRailsCoachId = (
  session: Session,
  coaches: { id: string; fullName?: string }[] | undefined,
): string | null => {
  if (!coaches || coaches.length === 0) return null;
  const coachDisplayName = session.coach.fullName;
  if (coachDisplayName) {
    const matches = coaches.filter(
      (coach) => coach.fullName === coachDisplayName,
    );
    if (matches.length === 1 && matches[0]) return matches[0].id;
  }
  const soleCoach = coaches[0];
  if (coaches.length === 1 && soleCoach) return soleCoach.id;
  return null;
};

/** Which view the reschedule surface renders. Single owner of that decision. */
type ReschedulePresentation =
  'pending' | 'confirmed' | 'past' | 'speedbump' | 'scheduler';

export const MemberRescheduleSession: FC<MemberRescheduleSessionProps> = ({
  close,
  mode = 'appointment',
  onRescheduled,
  session,
}) => {
  const dispatch = useAppDispatch();
  const isFutureSeries = mode === 'futureSeries';
  const viewerTimezone = getViewerTimezone();
  const nativeAppointmentId = session.sourceAppointmentId;
  const acknowledgementKey = `${nativeAppointmentId ?? ''}:${session.id}`;
  const [baselineDriftDetected, setBaselineDriftDetected] = useState(false);
  const [acknowledgedSessionKey, setAcknowledgedSessionKey] = useState<
    string | null
  >(null);
  const [
    acknowledgementRequiredByMutation,
    setAcknowledgementRequiredByMutation,
  ] = useState(false);
  const lateRescheduleAcknowledged =
    acknowledgedSessionKey === acknowledgementKey;
  useEffect(() => {
    setBaselineDriftDetected(false);
  }, [nativeAppointmentId, session.id]);
  const { data: currentUser } = useGetCurrentUserQuery(undefined, {
    skip: !nativeAppointmentId,
  });
  const railsCoachId = nativeAppointmentId
    ? resolveRailsCoachId(session, currentUser?.coaches)
    : session.coachId;
  const nativeCoachIdentityMissing = Boolean(
    nativeAppointmentId && currentUser?.id && !railsCoachId,
  );
  const intentKey = rescheduleMutationFingerprint([
    nativeAppointmentId ? 'member-appointment' : 'member-session',
    session.userId,
    nativeAppointmentId ?? session.id,
  ]);
  const [pendingIntent, setPendingIntent] = useState(() =>
    getBoundRescheduleIntent(intentKey, session),
  );
  const pendingIntentProjected =
    pendingIntent?.selectedStartsAt === session.startedAt;
  const guardedStartsAt = pendingIntent?.originalStartsAt ?? session.startedAt;
  const sessionIsPast =
    !pendingIntentProjected && isSessionStarted(guardedStartsAt);
  const {
    data: rescheduleOptions,
    isError: isOptionsError,
    isLoading: isOptionsLoading,
    refetch: refetchRescheduleOptions,
  } = useGetSessionRescheduleOptionsQuery(
    { id: session.id, viewerTimezone },
    {
      skip: Boolean(nativeAppointmentId) || !session.id || sessionIsPast,
    },
  );
  // MEN-4687: For a native appointment, `session.userId` is populated from
  // `expected_identities.member.person_id` (Moments UUID) — same issue as
  // coachId. The backend's `selected_member` defaults to `current_user` when
  // `member_id` is blank, so omit memberId for the member's own reschedule
  // (the common /m/appointments path). The admin-schedules-for-member path
  // uses a different component and is unaffected.
  const nativeMemberId =
    nativeAppointmentId && currentUser?.id ? undefined : session.userId;
  const nativeOptions = useGetAppointmentSchedulingContextQuery(
    {
      appointmentId: nativeAppointmentId ?? '',
      coachId: railsCoachId ?? '',
      intent: 'reschedule',
      kind: session.isChemistryCall ? 'chemistry' : 'coaching',
      memberId: nativeMemberId,
      ...(isFutureSeries
        ? { rescheduleScope: 'this_and_following' as const }
        : {}),
      viewerTimezone,
      visibleWeeks: 3,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !nativeAppointmentId || !currentUser?.id || !railsCoachId,
    },
  );
  const hasFutureSeriesIdentity = (
    context: BrowserSchedulingContext | undefined,
    appointment?: { seriesMutationVersion?: number; version: number },
  ) => {
    const activeSeries = context?.activeSeries;
    const target = context?.rescheduleTarget;
    return Boolean(
      nativeAppointmentId &&
      session.sourceAppointmentSeriesId &&
      session.sourceAppointmentRescheduleScopes?.includes(
        'this_and_following',
      ) &&
      activeSeries?.id === session.sourceAppointmentSeriesId &&
      activeSeries.nextAppointmentId === nativeAppointmentId &&
      target?.id === nativeAppointmentId &&
      target.seriesId === activeSeries.id &&
      target.allowedScopes.includes('this_and_following') &&
      (!appointment ||
        (target.version === appointment.version &&
          target.seriesMutationVersion === appointment.seriesMutationVersion)),
    );
  };
  const hasFutureSeriesAuthority = (
    context: BrowserSchedulingContext | undefined,
    appointment?: { seriesMutationVersion?: number; version: number },
  ) =>
    Boolean(
      context?.eligibility.eligible &&
      hasFutureSeriesIdentity(context, appointment),
    );
  const futureSeriesAuthority = (() => {
    if (!isFutureSeries) return true;
    if (
      nativeOptions.isError ||
      nativeOptions.isFetching ||
      nativeOptions.isLoading
    ) {
      return false;
    }
    return hasFutureSeriesAuthority(nativeOptions.data);
  })();
  const memberEmail = nativeOptions.data?.member.email ?? session.user.email;
  const [rescheduleSession, { isLoading: isSubmitting }] =
    useRescheduleSessionMutation();
  const [rescheduleAppointment, { isLoading: isNativeSubmitting }] =
    useRescheduleAppointmentMutation();
  const loadedOptions: { coachConflictStarts?: string[]; startsAt: string }[] =
    nativeAppointmentId
      ? (nativeOptions.data?.availability.days.flatMap((day) =>
          day.slots.map((slot) => ({
            coachConflictStarts: slotCoachConflictStarts(
              slot,
              day.appointmentCount,
              nativeOptions.data?.cadenceWeeks,
            ),
            startsAt: slot.startAt,
          })),
        ) ?? [])
      : (rescheduleOptions?.options ?? []);
  const options =
    pendingIntent &&
    !loadedOptions.some(
      (option) => option.startsAt === pendingIntent.selectedStartsAt,
    )
      ? [...loadedOptions, { startsAt: pendingIntent.selectedStartsAt }]
      : loadedOptions;
  const enabledDates = Array.from(
    new Set(options.map((option) => localDate(option.startsAt))),
  ).sort();
  const availableMonths = Array.from(
    new Set(enabledDates.map((date) => date.slice(0, 7))),
  );
  const [visibleMonth, setVisibleMonth] = useState(
    pendingIntent ? localDate(pendingIntent.selectedStartsAt).slice(0, 7) : '',
  );
  const resolvedVisibleMonth = visibleMonth || availableMonths[0] || '';
  const visibleMonthIndex = availableMonths.indexOf(resolvedVisibleMonth);
  const days: { date: string; enabled: boolean }[] = [];
  if (resolvedVisibleMonth) {
    const cursor = new Date(`${resolvedVisibleMonth}-01T00:00:00`);
    const end = new Date(cursor);
    end.setMonth(end.getMonth() + 1, 0);
    const enabledDateSet = new Set(enabledDates);
    while (cursor <= end) {
      const date = localDate(cursor.toISOString());
      days.push({ date, enabled: enabledDateSet.has(date) });
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  const [selectedDate, setSelectedDate] = useState(
    pendingIntent ? localDate(pendingIntent.selectedStartsAt) : '',
  );
  const [selectedStartsAt, setSelectedStartsAt] = useState(
    pendingIntent?.selectedStartsAt ?? '',
  );
  const [isConfirming, setIsConfirming] = useState(false);
  // MEN-5652: the member's optional note to the coach, quoted in the coach's reschedule email.
  const [note, setNote] = useState('');
  const [pendingOperationId, setPendingOperationId] = useState<string | null>(
    null,
  );
  const [
    pendingOperationRetryAfterSeconds,
    setPendingOperationRetryAfterSeconds,
  ] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [operationReviewRequired, setOperationReviewRequired] = useState(false);
  const [isSubmitPending, setIsSubmitPending] = useState(false);
  const [isRecoveringRetainedCommand, setIsRecoveringRetainedCommand] =
    useState(false);
  const [pendingPresentation, setPendingPresentation] = useState(false);
  const pendingPresentationRef = useRef(false);
  const [submissionStartedAt, setSubmissionStartedAt] = useState<number | null>(
    null,
  );
  const submissionStartedAtRef = useRef<number | null>(null);
  const [confirmedSchedule, setConfirmedSchedule] = useState<Pick<
    DecodedBrowserAppointment['currentSchedule'],
    'startAt' | 'timezone'
  > | null>(null);
  const lifecycleOwnerKey = nativeAppointmentId
    ? `member-reschedule:${session.id}:${nativeAppointmentId}`
    : undefined;
  const presentationOwnerRef = useRef(lifecycleOwnerKey);
  const ownerGenerationRef = useRef(0);
  const submitInFlightRef = useRef(false);
  const submissionCreatedHereRef = useRef(false);
  const restoredPendingAppliedRef = useRef(false);
  const confirmationHandledRef = useRef(false);
  const [getAppointment] = useLazyGetAppointmentQuery();
  const displayedFutureSeriesBaseline =
    nativeAppointmentId &&
    session.sourceAppointmentSeriesId &&
    typeof session.sourceAppointmentSeriesMutationVersion === 'number' &&
    typeof session.sourceAppointmentVersion === 'number' &&
    pendingIntent?.selectedStartsAt
      ? {
          appointmentId: nativeAppointmentId,
          expectedSeriesMutationVersion:
            session.sourceAppointmentSeriesMutationVersion,
          expectedVersion: session.sourceAppointmentVersion,
          seriesId: session.sourceAppointmentSeriesId,
          startAt: pendingIntent.selectedStartsAt,
          viewerTimezone,
        }
      : null;
  const confirmsFutureSeriesUpdate = (
    appointment: DecodedBrowserAppointment,
    baseline: FutureSeriesRescheduleCommand | null,
  ) =>
    !isFutureSeries ||
    (baseline !== null &&
      appointment.id === baseline.appointmentId &&
      appointment.seriesId === baseline.seriesId &&
      appointment.seriesMutationVersion >
        baseline.expectedSeriesMutationVersion &&
      appointment.version > baseline.expectedVersion &&
      Date.parse(appointment.currentSchedule.startAt) ===
        Date.parse(baseline.startAt));
  const readTargetAppointment = async (
    baseline = displayedFutureSeriesBaseline,
  ) => {
    const request = getAppointment({
      appointmentId: baseline?.appointmentId ?? nativeAppointmentId ?? '',
    });
    try {
      const appointment = await request.unwrap();
      return appointment.id ===
        (baseline?.appointmentId ?? nativeAppointmentId) &&
        appointment.kind === 'coaching' &&
        appointment.seriesId ===
          (baseline?.seriesId ?? session.sourceAppointmentSeriesId) &&
        confirmsFutureSeriesUpdate(appointment, baseline)
        ? appointment
        : null;
    } finally {
      request.unsubscribe();
    }
  };
  const readConfirmedAppointmentReschedule = async (
    baseline: AppointmentRescheduleBaseline,
  ) => {
    const request = getAppointment({ appointmentId: baseline.appointmentId });
    try {
      const appointment = await request.unwrap();
      const seriesFenceMatches = baseline.expectedSeriesId
        ? typeof baseline.expectedSeriesMutationVersion === 'number' &&
          typeof appointment.seriesMutationVersion === 'number' &&
          appointment.seriesMutationVersion >
            baseline.expectedSeriesMutationVersion
        : appointment.seriesMutationVersion ===
          baseline.expectedSeriesMutationVersion;
      return appointment.id === baseline.appointmentId &&
        appointment.kind === baseline.expectedKind &&
        appointment.seriesId === baseline.expectedSeriesId &&
        seriesFenceMatches &&
        appointment.version > baseline.expectedVersion &&
        Date.parse(appointment.currentSchedule.startAt) ===
          Date.parse(baseline.startAt)
        ? appointment
        : null;
    } finally {
      request.unsubscribe();
    }
  };
  const appointmentCapability = getAppointmentBrowserCapability();
  const schedulingContext = nativeOptions.data;
  const schedulingTarget = schedulingContext?.rescheduleTarget;
  const [reviewedSchedulingContext, setReviewedSchedulingContext] =
    useState<BrowserSchedulingContext | null>(null);
  useEffect(() => {
    if (
      nativeOptions.isError ||
      nativeOptions.isFetching ||
      !nativeAppointmentId ||
      schedulingTarget?.id !== nativeAppointmentId
    ) {
      return;
    }
    setReviewedSchedulingContext(schedulingContext ?? null);
  }, [
    nativeAppointmentId,
    nativeOptions.isError,
    nativeOptions.isFetching,
    schedulingContext,
    schedulingTarget?.id,
  ]);
  const authoritativeSchedulingContext =
    nativeAppointmentId &&
    !nativeOptions.isError &&
    !nativeOptions.isFetching &&
    !nativeOptions.isLoading &&
    reviewedSchedulingContext?.rescheduleTarget?.id === nativeAppointmentId
      ? reviewedSchedulingContext
      : null;
  const authoritativeSchedulingTarget =
    authoritativeSchedulingContext?.rescheduleTarget ?? null;
  const nativeRescheduleScope = isFutureSeries
    ? 'this_and_following'
    : 'appointment';
  const nativeRescheduleUnavailable = Boolean(
    authoritativeSchedulingContext &&
    (('eligibility' in authoritativeSchedulingContext &&
      !authoritativeSchedulingContext.eligibility.eligible) ||
      (authoritativeSchedulingTarget &&
        'allowedScopes' in authoritativeSchedulingTarget &&
        !authoritativeSchedulingTarget.allowedScopes.includes(
          nativeRescheduleScope,
        ))),
  );
  const authoritativeSchedulingTargetSchedule =
    authoritativeSchedulingTarget?.currentSchedule;
  const displayedSession = authoritativeSchedulingTargetSchedule
    ? ({
        ...session,
        endedAt: authoritativeSchedulingTargetSchedule.endAt,
        startedAt: authoritativeSchedulingTargetSchedule.startAt,
      } as Session)
    : session;
  let authoritativeStartsAt: string | undefined = guardedStartsAt;
  if (nativeAppointmentId) {
    authoritativeStartsAt = authoritativeSchedulingTarget
      ? (authoritativeSchedulingTargetSchedule?.startAt ?? guardedStartsAt)
      : undefined;
  }
  const isPast =
    !pendingIntentProjected &&
    Boolean(authoritativeStartsAt && isSessionStarted(authoritativeStartsAt));
  const requiresWithin48HoursConfirmation = nativeAppointmentId
    ? Boolean(
        authoritativeStartsAt &&
        isWithinRescheduleWindow(authoritativeStartsAt),
      )
    : Boolean(
        rescheduleOptions?.requiresWithin48HoursConfirmation ||
        acknowledgementRequiredByMutation,
      );
  const showLateRescheduleSpeedbump =
    !pendingIntentProjected &&
    requiresWithin48HoursConfirmation &&
    !lateRescheduleAcknowledged;
  const isNativeBaselinePending = Boolean(
    nativeAppointmentId &&
    (!authoritativeSchedulingTarget ||
      nativeOptions.isLoading ||
      nativeOptions.isFetching),
  );
  const lifecycle = useAppointmentLifecycleGuard({
    allowReviewedPreimage: false,
    capability: appointmentCapability,
    displayed: authoritativeSchedulingTarget
      ? {
          id: authoritativeSchedulingTarget.id,
          seriesId: authoritativeSchedulingTarget.seriesId,
          seriesMutationVersion:
            authoritativeSchedulingTarget.seriesMutationVersion,
          version: authoritativeSchedulingTarget.version,
        }
      : {
          id: nativeAppointmentId,
          seriesId: session.sourceAppointmentSeriesId,
          seriesMutationVersion: session.sourceAppointmentSeriesMutationVersion,
          version: session.sourceAppointmentVersion,
        },
    ownerKey: lifecycleOwnerKey,
    protocolMarker: PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
    readAppointment: async () => {
      const request = getAppointment({
        appointmentId: nativeAppointmentId ?? '',
      });
      return request.unwrap().finally(() => request.unsubscribe());
    },
  });
  const {
    clearOwnedOperation,
    consumeConfirmedReceipt,
    currentCommand,
    recordConfirmed,
    rereadAfterRejection,
  } = lifecycle;
  const restoredSubmittingAtMount = useRef(
    lifecycle.restoredSubmitting,
  ).current;
  const selectedDateOptions = options.filter(
    (option) => localDate(option.startsAt) === selectedDate,
  );
  const selectedCoachConflictStarts =
    options.find((option) => option.startsAt === selectedStartsAt)
      ?.coachConflictStarts ?? [];
  const operationStatus = useGetAppointmentOperationQuery(
    {
      browserContractMarker: PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
      operationId: pendingOperationId ?? '',
    },
    {
      pollingInterval: upgradeRequired
        ? 0
        : Math.max(1000, (pendingOperationRetryAfterSeconds ?? 5) * 1000),
      skip:
        !pendingOperationId ||
        upgradeRequired ||
        operationReviewRequired ||
        pendingPresentation,
    },
  );
  const requestClose = () => {
    if (confirmedSchedule) return;
    if (pendingPresentation) {
      close();
      return;
    }
    if (
      pendingOperationId ||
      lifecycle.isReading ||
      isRecoveringRetainedCommand ||
      submitInFlightRef.current ||
      isSubmitPending ||
      isSubmitting ||
      isNativeSubmitting
    )
      return;
    close();
  };

  const showPendingPresentation = useCallback(() => {
    pendingPresentationRef.current = true;
    setPendingPresentation(true);
    setIsSubmitPending(false);
  }, []);
  const confirmationMayRender = useCallback(() => {
    if (pendingPresentationRef.current) return false;
    const startedAt = submissionStartedAtRef.current;
    if (
      startedAt !== null &&
      Date.now() - startedAt >= PENDING_PRESENTATION_DELAY_MS
    ) {
      showPendingPresentation();
      return false;
    }
    return true;
  }, [showPendingPresentation]);

  useEffect(() => {
    if (presentationOwnerRef.current === lifecycleOwnerKey) return;
    presentationOwnerRef.current = lifecycleOwnerKey;
    ownerGenerationRef.current += 1;
    pendingPresentationRef.current = false;
    submissionStartedAtRef.current = null;
    submitInFlightRef.current = false;
    submissionCreatedHereRef.current = false;
    restoredPendingAppliedRef.current = false;
    confirmationHandledRef.current = false;
    setPendingPresentation(false);
    setSubmissionStartedAt(null);
    setIsSubmitPending(false);
    setIsRecoveringRetainedCommand(false);
    setPendingOperationId(null);
    setPendingOperationRetryAfterSeconds(null);
    setSubmitError(null);
    setUpgradeRequired(false);
    setOperationReviewRequired(false);
    setConfirmedSchedule(null);
    setAcknowledgementRequiredByMutation(false);
    const restoredIntent = getBoundRescheduleIntent(intentKey, session);
    setPendingIntent(restoredIntent);
    setSelectedDate(
      restoredIntent ? localDate(restoredIntent.selectedStartsAt) : '',
    );
    setSelectedStartsAt(restoredIntent?.selectedStartsAt ?? '');
    setIsConfirming(false);
    // A note belongs to one appointment: never carry a draft to another. The restore effect
    // below, which runs after this one, puts back only the new owner's own retained note.
    setNote('');
  }, [intentKey, lifecycleOwnerKey, session]);

  useEffect(() => {
    if (!isFutureSeries || lifecycle.confirmedReceipt) return;
    const restored =
      lifecycle.restoredSubmitting ??
      lifecycle.restoredPending ??
      lifecycle.rejected;
    if (!restored) return;
    setIsSubmitPending(true);
    if (!restored.submittedAt) {
      showPendingPresentation();
      return;
    }
    const startedAt = Date.parse(restored.submittedAt);
    submissionStartedAtRef.current = startedAt;
    setSubmissionStartedAt(startedAt);
  }, [
    isFutureSeries,
    lifecycle.confirmedReceipt,
    lifecycle.rejected,
    lifecycle.restoredPending,
    lifecycle.restoredSubmitting,
    showPendingPresentation,
  ]);

  useEffect(() => {
    if (!isFutureSeries || submissionStartedAt === null || confirmedSchedule)
      return undefined;
    const elapsed = Date.now() - submissionStartedAt;
    if (elapsed < 0 || elapsed >= PENDING_PRESENTATION_DELAY_MS) {
      showPendingPresentation();
      return undefined;
    }
    const timeout = window.setTimeout(
      showPendingPresentation,
      PENDING_PRESENTATION_DELAY_MS - elapsed,
    );
    return () => window.clearTimeout(timeout);
  }, [
    confirmedSchedule,
    isFutureSeries,
    showPendingPresentation,
    submissionStartedAt,
  ]);

  useEffect(() => {
    if (lifecycle.restoredPending && !restoredPendingAppliedRef.current) {
      restoredPendingAppliedRef.current = true;
      setPendingOperationId(lifecycle.restoredPending.operationId);
      setPendingOperationRetryAfterSeconds(
        lifecycle.restoredPending.retryAfterSeconds,
      );
    }
  }, [lifecycle.restoredPending]);

  // A retained submission's note is part of its bytes: put it back in the field so the
  // member sees it and an unchanged retry resends it with the original idempotency key.
  const restoredSubmittingBytes = lifecycle.restoredSubmitting?.bytes;
  useEffect(() => {
    const restoredNote = retainedRescheduleNote(
      restoredSubmittingBytes,
      nativeAppointmentId,
    );
    if (restoredNote !== null) setNote(restoredNote);
  }, [nativeAppointmentId, restoredSubmittingBytes]);

  useEffect(() => {
    if (
      isFutureSeries ||
      !restoredSubmittingAtMount ||
      confirmationHandledRef.current
    ) {
      return undefined;
    }
    const baseline = retainedAppointmentRescheduleBaseline(
      restoredSubmittingAtMount.bytes,
      session.isChemistryCall ? 'chemistry' : 'coaching',
    );
    if (!baseline || baseline.appointmentId !== nativeAppointmentId) {
      return undefined;
    }

    let active = true;
    let reconciled = false;
    const ownerGeneration = ownerGenerationRef.current;
    confirmationHandledRef.current = true;
    setIsRecoveringRetainedCommand(true);
    readConfirmedAppointmentReschedule(baseline)
      .then((appointment) => {
        if (
          !active ||
          ownerGenerationRef.current !== ownerGeneration ||
          !appointment
        )
          return;
        const handled = recordConfirmed();
        if (!handled) return;
        reconciled = true;
        clearRescheduleMutationIntent(intentKey);
        setPendingIntent(null);
        dispatch(api.util.invalidateTags(['Appointment', 'Session']));
        onRescheduled?.();
        close();
      })
      .catch(() => undefined)
      .finally(() => {
        if (!active || ownerGenerationRef.current !== ownerGeneration) return;
        setIsRecoveringRetainedCommand(false);
        if (!reconciled) confirmationHandledRef.current = false;
      });

    return () => {
      active = false;
      if (!reconciled) confirmationHandledRef.current = false;
    };
  }, [
    close,
    dispatch,
    intentKey,
    isFutureSeries,
    nativeAppointmentId,
    onRescheduled,
    recordConfirmed,
    restoredSubmittingAtMount,
    session.isChemistryCall,
  ]);

  useEffect(() => {
    if (!isFutureSeries || confirmationHandledRef.current) return undefined;
    const confirmed = lifecycle.confirmedReceipt;
    const receipt =
      confirmed ??
      (submissionCreatedHereRef.current ? null : lifecycle.restoredSubmitting);
    if (!receipt) return undefined;

    const baseline = futureSeriesRescheduleCommand(receipt.bytes);
    if (
      !baseline ||
      baseline.appointmentId !== nativeAppointmentId ||
      baseline.seriesId !== session.sourceAppointmentSeriesId
    ) {
      return undefined;
    }

    let active = true;
    let reconciled = Boolean(confirmed);
    const ownerGeneration = ownerGenerationRef.current;
    const receiptSchedule = {
      startAt: baseline.startAt,
      timezone: baseline.viewerTimezone,
    };
    confirmationHandledRef.current = true;
    setIsRecoveringRetainedCommand(!confirmed);
    readTargetAppointment(baseline)
      .then((appointment) => {
        if (!active || ownerGenerationRef.current !== ownerGeneration) return;
        if (confirmed) {
          const schedule = appointment?.currentSchedule ?? receiptSchedule;
          if (confirmationMayRender()) {
            setConfirmedSchedule(schedule);
          }
          return;
        }
        if (!appointment) return;
        reconciled = true;
        const handled = recordConfirmed({ retainReceipt: true });
        if (!handled) {
          reconciled = false;
          return;
        }
        dispatch(api.util.invalidateTags(['Appointment', 'Session']));
        if (confirmationMayRender()) {
          setConfirmedSchedule(appointment.currentSchedule);
        }
      })
      .catch(() => {
        if (!active || ownerGenerationRef.current !== ownerGeneration) return;
        if (confirmed && confirmationMayRender()) {
          setConfirmedSchedule(receiptSchedule);
        }
      })
      .finally(() => {
        if (!active || ownerGenerationRef.current !== ownerGeneration) return;
        setIsRecoveringRetainedCommand(false);
        if (!reconciled) confirmationHandledRef.current = false;
      });

    return () => {
      active = false;
      if (!reconciled) confirmationHandledRef.current = false;
    };
  }, [
    confirmationMayRender,
    dispatch,
    isFutureSeries,
    lifecycle.confirmedReceipt,
    lifecycle.restoredSubmitting,
    nativeAppointmentId,
    recordConfirmed,
    session.sourceAppointmentSeriesId,
    showPendingPresentation,
  ]);

  useEffect(() => {
    if (!lifecycle.confirmedReceipt) return;
    if (isFutureSeries) return;
    if (!consumeConfirmedReceipt()) return;
    clearRescheduleMutationIntent(intentKey);
    setPendingIntent(null);
    onRescheduled?.();
    close();
  }, [
    close,
    consumeConfirmedReceipt,
    confirmationMayRender,
    isFutureSeries,
    intentKey,
    lifecycle.confirmedReceipt,
    onRescheduled,
    nativeAppointmentId,
  ]);

  useEffect(() => {
    if (appointmentUpgradeGuardSpent(operationStatus.error)) {
      setUpgradeRequired(true);
    }
  }, [operationStatus.error]);

  useEffect(() => {
    const operation = operationStatus.currentData;
    if (!operation || operation.id !== pendingOperationId) return;
    if (operation.status === 'pending') {
      setPendingOperationRetryAfterSeconds(operation.retryAfterSeconds ?? null);
    }
    if (operation.status === 'confirmed') {
      if (!operation.appointmentIds.includes(nativeAppointmentId ?? '')) {
        setOperationReviewRequired(true);
        setPendingOperationId(null);
        setPendingOperationRetryAfterSeconds(null);
        submitInFlightRef.current = false;
        setSubmitError(
          'The confirmed operation does not match this appointment. The reschedule remains saved for review.',
        );
        return;
      }
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      setIsSubmitPending(true);
      if (isFutureSeries) {
        if (confirmationHandledRef.current) return;
        confirmationHandledRef.current = true;
      }
      const ownerGeneration = ownerGenerationRef.current;
      const finishConfirmation = async () => {
        const baseline = futureSeriesRescheduleCommand(currentCommand()?.bytes);
        const appointment = isFutureSeries
          ? await readTargetAppointment(baseline)
          : null;
        if (ownerGenerationRef.current !== ownerGeneration) return;
        if (isFutureSeries && !appointment) {
          setPendingOperationId(null);
          setPendingOperationRetryAfterSeconds(null);
          submitInFlightRef.current = false;
          confirmationHandledRef.current = false;
          return;
        }
        const handled = recordConfirmed({ retainReceipt: isFutureSeries });
        setPendingOperationId(null);
        setPendingOperationRetryAfterSeconds(null);
        submitInFlightRef.current = false;
        if (!isFutureSeries) setIsSubmitPending(false);
        if (!handled) {
          if (isFutureSeries) {
            confirmationHandledRef.current = false;
          }
          return;
        }
        if (appointment && confirmationMayRender()) {
          setIsSubmitPending(false);
          setConfirmedSchedule(appointment.currentSchedule);
          return;
        }
        if (appointment) return;
        clearRescheduleMutationIntent(intentKey);
        setPendingIntent(null);
        onRescheduled?.();
        close();
      };
      finishConfirmation().catch(() => {
        if (ownerGenerationRef.current !== ownerGeneration) return;
        confirmationHandledRef.current = false;
        setPendingOperationId(null);
        setPendingOperationRetryAfterSeconds(null);
        submitInFlightRef.current = false;
        if (!isFutureSeries) setIsSubmitPending(false);
      });
    }
    if (operation.status === 'rejected') {
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      submitInFlightRef.current = false;
      if (!isFutureSeries) setIsSubmitPending(false);
      const command = currentCommand();
      if (command) {
        rereadAfterRejection({
          ...command,
          operationId: operation.id,
        }).catch(() => undefined);
      }
      if (!isFutureSeries) {
        setSubmitError(
          appointmentRejectionMessage(
            operation.rejection,
            'reschedule',
            'We could not reschedule this session. Please try again.',
          ),
        );
      }
    }
  }, [
    close,
    confirmationMayRender,
    intentKey,
    onRescheduled,
    operationStatus.currentData,
    pendingOperationId,
    rereadAfterRejection,
    recordConfirmed,
    currentCommand,
    isFutureSeries,
    nativeAppointmentId,
  ]);

  const onConfirm = async (retryRestoredSubmitting = false) => {
    if (!selectedStartsAt) return;
    const inMemoryIntent =
      pendingIntent && isRescheduleIntentBound(pendingIntent, session)
        ? pendingIntent
        : null;
    if (pendingIntent && !inMemoryIntent) {
      clearRescheduleMutationIntent(intentKey);
      setPendingIntent(null);
      setSelectedStartsAt('');
      setIsConfirming(false);
      return;
    }
    const storedIntent = getBoundRescheduleIntent(intentKey, session);
    const resumableIntent = storedIntent ?? inMemoryIntent;
    const resumesPendingIntent =
      resumableIntent?.selectedStartsAt === selectedStartsAt;
    if (!authoritativeStartsAt) {
      setIsConfirming(false);
      return;
    }
    let submitGuardStartsAt = authoritativeStartsAt;
    if (!nativeAppointmentId && resumesPendingIntent) {
      submitGuardStartsAt = resumableIntent.originalStartsAt;
    }
    const resumedIntentProjected =
      resumesPendingIntent &&
      resumableIntent.selectedStartsAt === session.startedAt;
    const requiresAcknowledgementAtSubmit = nativeAppointmentId
      ? isWithinRescheduleWindow(submitGuardStartsAt)
      : requiresWithin48HoursConfirmation;
    if (
      !resumedIntentProjected &&
      (isSessionStarted(submitGuardStartsAt) ||
        (requiresAcknowledgementAtSubmit && !lateRescheduleAcknowledged))
    ) {
      setIsConfirming(false);
      return;
    }
    if (
      submitInFlightRef.current ||
      isRecoveringRetainedCommand ||
      pendingOperationId ||
      baselineDriftDetected ||
      operationReviewRequired ||
      upgradeRequired
    )
      return;
    submitInFlightRef.current = true;
    setIsSubmitPending(true);
    setSubmitError(null);
    setPendingOperationRetryAfterSeconds(null);
    setPendingOperationId(null);
    let submittedNativeBaseline: AppointmentRescheduleBaseline | null = null;
    if (resumableIntent && !resumesPendingIntent) {
      clearRescheduleMutationIntent(intentKey);
    }
    const intent = resumesPendingIntent
      ? resumableIntent
      : getRescheduleMutationIntent(intentKey, () => ({
          fingerprint: rescheduleMutationFingerprint([
            'member-chemistry-or-biweekly',
            session.userId,
            session.id,
            session.startedAt,
            selectedStartsAt,
          ]),
          mutationId: window.crypto.randomUUID(),
          originalStartsAt: session.startedAt,
          selectedStartsAt,
        }));
    setPendingIntent(intent);
    const ownerGeneration = ownerGenerationRef.current;
    let futureSeriesSubmissionRecorded = false;
    try {
      if (nativeAppointmentId) {
        const restoredBytes =
          isFutureSeries && retryRestoredSubmitting
            ? lifecycle.restoredSubmitting?.bytes
            : undefined;
        const restoredBaseline = futureSeriesRescheduleCommand(restoredBytes);
        if (isFutureSeries && retryRestoredSubmitting && !restoredBaseline) {
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          setSubmitError(
            'The saved reschedule could not be validated. Contact support before retrying.',
          );
          return;
        }
        if (restoredBaseline) {
          try {
            const appliedAppointment =
              await readTargetAppointment(restoredBaseline);
            if (ownerGenerationRef.current !== ownerGeneration) return;
            if (appliedAppointment) {
              confirmationHandledRef.current = true;
              const handled = recordConfirmed({ retainReceipt: true });
              submitInFlightRef.current = false;
              setIsSubmitPending(false);
              if (!handled) {
                confirmationHandledRef.current = false;
                setOperationReviewRequired(true);
                setSubmitError(
                  'We could not safely save this confirmation. Reload to recover the saved operation.',
                );
                return;
              }
              dispatch(api.util.invalidateTags(['Appointment', 'Session']));
              if (confirmationMayRender()) {
                setConfirmedSchedule(appliedAppointment.currentSchedule);
              }
              return;
            }
          } catch {
            if (ownerGenerationRef.current !== ownerGeneration) return;
            // Continue to the guarded unchanged retry path.
          }
        }
        const preflight = await lifecycle.read();
        if (ownerGenerationRef.current !== ownerGeneration) return;
        if (preflight.kind !== 'ready') {
          if (preflight.kind !== 'upgrade') await nativeOptions.refetch();
          if (ownerGenerationRef.current !== ownerGeneration) return;
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          if (preflight.kind === 'upgrade') setUpgradeRequired(true);
          else {
            if (preflight.preimage) setBaselineDriftDetected(true);
            setSubmitError(
              'Appointment details changed. Close and reopen this dialog to review the latest session before trying again.',
            );
          }
          return;
        }
        const appointment = preflight.preimage;
        if (
          restoredBaseline &&
          (restoredBaseline.appointmentId !== appointment.id ||
            restoredBaseline.seriesId !== appointment.seriesId ||
            restoredBaseline.expectedVersion !== appointment.version ||
            restoredBaseline.expectedSeriesMutationVersion !==
              appointment.seriesMutationVersion ||
            restoredBaseline.startAt !== selectedStartsAt)
        ) {
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          setSubmitError(
            'The changed request cannot replace the saved reschedule. Restore the original time or contact support.',
          );
          return;
        }
        let refreshedContext: BrowserSchedulingContext | undefined;
        try {
          const refreshResult = await nativeOptions.refetch();
          if (ownerGenerationRef.current !== ownerGeneration) return;
          refreshedContext = refreshResult.error
            ? undefined
            : refreshResult.data;
        } catch {
          if (ownerGenerationRef.current !== ownerGeneration) return;
          refreshedContext = undefined;
        }
        const target = refreshedContext?.rescheduleTarget;
        const selectedSlotStillAvailable =
          refreshedContext?.availability.days.some((day) =>
            day.slots.some((slot) => slot.startAt === selectedStartsAt),
          ) ?? false;
        const targetMatchesAppointment = Boolean(
          target?.id === nativeAppointmentId &&
          target.version === appointment.version &&
          target.seriesId === appointment.seriesId &&
          target.seriesMutationVersion === appointment.seriesMutationVersion,
        );
        const hasCurrentAuthority = isFutureSeries
          ? hasFutureSeriesAuthority(refreshedContext, appointment)
          : Boolean(
              refreshedContext?.eligibility.eligible &&
              targetMatchesAppointment &&
              target?.allowedScopes.includes('appointment'),
            );
        if (!hasCurrentAuthority || !selectedSlotStillAvailable) {
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          setSubmitError(
            'Appointment details changed. Review the refreshed session and confirm again.',
          );
          return;
        }
        const command = restoredBaseline
          ? { ...restoredBaseline, scope: 'this_and_following' as const }
          : {
              appointmentId: nativeAppointmentId,
              expectedVersion: appointment.version,
              scope: isFutureSeries
                ? ('this_and_following' as const)
                : ('appointment' as const),
              startAt: selectedStartsAt,
              viewerTimezone,
              ...appointmentSeriesCommandFields(
                appointmentCapability,
                appointment,
              ),
              // A blank note is no note: the key is left off, so a note-less command keeps
              // exactly the bytes (and idempotency) it had before notes existed.
              ...(note.trim() ? { reason: note.trim() } : {}),
            };
        const bytes =
          restoredBaseline && restoredBytes
            ? restoredBytes
            : canonicalAppointmentCommand(
                isFutureSeries
                  ? {
                      ...command,
                      coachPersonId: session.coach.id,
                      memberPersonId: session.user.id,
                    }
                  : command,
              );
        const confirmationBaseline = isFutureSeries
          ? futureSeriesRescheduleCommand(bytes)
          : null;
        if (lifecycle.submissionBlocked(bytes)) {
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          setSubmitError(
            'This rejected reschedule is unchanged. Review the refreshed session and choose a different time before confirming again.',
          );
          return;
        }
        const submission = lifecycle.submissionFor(
          bytes,
          retryRestoredSubmitting,
        );
        if (submission.kind === 'review') {
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          setSubmitError(
            submission.reason === 'changed-command'
              ? 'The changed request cannot replace the saved reschedule. Restore the original time or contact support.'
              : 'The previous reschedule outcome is unknown. Use the explicit unchanged retry or contact support.',
          );
          return;
        }
        const { idempotencyKey } = submission;
        if (!lifecycle.recordSubmitting({ bytes, idempotencyKey })) {
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          setSubmitError(
            'We could not safely save this reschedule. Please enable session storage and try again.',
          );
          return;
        }
        submissionCreatedHereRef.current = true;
        submittedNativeBaseline = {
          appointmentId: appointment.id,
          expectedKind: session.isChemistryCall ? 'chemistry' : 'coaching',
          expectedSeriesId: appointment.seriesId,
          expectedSeriesMutationVersion: appointment.seriesMutationVersion,
          expectedVersion: appointment.version,
          startAt: selectedStartsAt,
        };
        if (isFutureSeries) {
          const startedAt = lifecycle.currentSubmissionStartedAt();
          const startedAtMillis = startedAt
            ? Date.parse(startedAt)
            : Date.now();
          submissionStartedAtRef.current = startedAtMillis;
          setSubmissionStartedAt(startedAtMillis);
          futureSeriesSubmissionRecorded = true;
        }
        const result = await rescheduleAppointment({
          appointmentId: command.appointmentId,
          expectedSeriesMutationVersion:
            'expectedSeriesMutationVersion' in command
              ? command.expectedSeriesMutationVersion
              : null,
          expectedVersion: command.expectedVersion,
          idempotencyKey,
          scope: command.scope,
          seriesId: 'seriesId' in command ? command.seriesId : null,
          startAt: command.startAt,
          userId: currentUser?.id,
          viewerTimezone: command.viewerTimezone,
          ...('reason' in command && command.reason
            ? { reason: command.reason }
            : {}),
        }).unwrap();
        if (ownerGenerationRef.current !== ownerGeneration) return;
        if (result.status === 'pending') {
          setUpgradeRequired(false);
          lifecycle.recordPending({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
            retryAfterSeconds: result.retryAfterSeconds ?? null,
          });
          setPendingOperationId(result.operationId);
          setPendingOperationRetryAfterSeconds(
            result.retryAfterSeconds ?? null,
          );
          submitInFlightRef.current = false;
          if (!isFutureSeries) setIsSubmitPending(false);
          return;
        }
        if (result.status === 'rejected') {
          submitInFlightRef.current = false;
          if (!isFutureSeries) setIsSubmitPending(false);
          await rereadAfterRejection({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
          });
          if (ownerGenerationRef.current !== ownerGeneration) return;
          if (!isFutureSeries) {
            setSubmitError(
              appointmentRejectionMessage(
                result.rejection,
                'reschedule',
                'We could not reschedule this session. Please try again.',
              ),
            );
          }
          return;
        }
        if (isFutureSeries) {
          let confirmedTarget = result.appointments.find(
            ({ id }) => id === nativeAppointmentId,
          );
          if (
            !confirmedTarget ||
            confirmedTarget.kind !== 'coaching' ||
            confirmedTarget.seriesId !== session.sourceAppointmentSeriesId ||
            !confirmsFutureSeriesUpdate(confirmedTarget, confirmationBaseline)
          ) {
            try {
              confirmedTarget =
                (await readTargetAppointment(confirmationBaseline)) ??
                undefined;
              if (ownerGenerationRef.current !== ownerGeneration) return;
            } catch {
              if (ownerGenerationRef.current !== ownerGeneration) return;
              submitInFlightRef.current = false;
              return;
            }
          }
          if (!confirmedTarget) {
            submitInFlightRef.current = false;
            return;
          }
          confirmationHandledRef.current = true;
          const handled = recordConfirmed({ retainReceipt: true });
          submitInFlightRef.current = false;
          setIsSubmitPending(false);
          if (!handled) {
            confirmationHandledRef.current = false;
            setIsSubmitPending(true);
            return;
          }
          if (confirmationMayRender()) {
            setConfirmedSchedule(confirmedTarget.currentSchedule);
          }
          return;
        }
      } else {
        await rescheduleSession({
          confirmedWithin48Hours: lateRescheduleAcknowledged,
          id: session.id,
          mutationId: intent.mutationId,
          startsAt: selectedStartsAt,
          viewerTimezone,
        }).unwrap();
        if (ownerGenerationRef.current !== ownerGeneration) return;
      }
      clearRescheduleMutationIntent(intentKey);
      setPendingIntent(null);
      const handled = !nativeAppointmentId || recordConfirmed();
      submitInFlightRef.current = false;
      setIsSubmitPending(false);
      if (!handled) return;
      onRescheduled?.();
      close();
    } catch (error) {
      if (ownerGenerationRef.current !== ownerGeneration) return;
      submitInFlightRef.current = false;
      if (futureSeriesSubmissionRecorded) return;
      setIsSubmitPending(false);
      if (!nativeAppointmentId && isWithin48HoursAcknowledgementError(error)) {
        setAcknowledgementRequiredByMutation(true);
        setIsConfirming(false);
        try {
          await refetchRescheduleOptions();
        } catch {
          // The mutation response is itself authoritative for this warning.
        }
        return;
      }
      const upgradeGuardSpent = appointmentUpgradeGuardSpent(error);
      const upgradeRetainsOwnership =
        upgradeGuardSpent ||
        (error as { upgrade?: { state?: unknown } }).upgrade?.state ===
          'reload-requested';
      if (upgradeGuardSpent) setUpgradeRequired(true);
      if (
        nativeAppointmentId &&
        !isFutureSeries &&
        !upgradeRetainsOwnership &&
        submittedNativeBaseline &&
        shouldReconcileAppointmentWrite(error)
      ) {
        try {
          const reconciledAppointment =
            await readConfirmedAppointmentReschedule(submittedNativeBaseline);
          if (reconciledAppointment) {
            clearRescheduleMutationIntent(intentKey);
            setPendingIntent(null);
            const handled = recordConfirmed();
            if (!handled) return;
            dispatch(api.util.invalidateTags(['Appointment', 'Session']));
            onRescheduled?.();
            close();
            return;
          }
        } catch {
          // The retained receipt prevents an unsafe retry after an unknown write.
        }
        setSubmitError(
          'We could not verify whether this reschedule was saved. Reload before trying again.',
        );
        return;
      }
      if (
        nativeAppointmentId &&
        !upgradeRetainsOwnership &&
        !shouldReconcileAppointmentWrite(error)
      ) {
        clearOwnedOperation();
      }
      if (isNonRetryableClientError(error)) {
        clearRescheduleMutationIntent(intentKey);
        setPendingIntent(null);
      }
      // A declared non-write: the scheduling service refused the operation and
      // wrote nothing, so the member is told what is unavailable and that their
      // session is untouched, rather than the generic retry line.
      const declined = trustedAppointmentDeclineMessage('reschedule', error);
      if (declined) {
        setSubmitError(declined);
        return;
      }
      setSubmitError('We could not reschedule this session. Please try again.');
    }
  };

  const retainSubmittedPresentation =
    isFutureSeries && submissionStartedAt !== null && isSubmitPending;
  let optionsContent: ReactNode;
  if (
    isOptionsLoading ||
    (!retainSubmittedPresentation &&
      (nativeOptions.isLoading || (isFutureSeries && nativeOptions.isFetching)))
  ) {
    optionsContent = (
      <div className="flex flex-1 items-center justify-center">
        <CircularProgress />
      </div>
    );
  } else if (
    // MEN-3999: A single-session availability load failure (e.g. a transient
    // scheduling-context timeout) must not surface a red error that makes an
    // otherwise-working reschedule flow look broken. Those cases fall through
    // to the neutral empty state below. The future-series and missing
    // coach-identity cases are distinct blocking conditions and still error.
    nativeCoachIdentityMissing ||
    (isFutureSeries &&
      (isOptionsError ||
        (!retainSubmittedPresentation && nativeOptions.isError) ||
        (!futureSeriesAuthority && !isSubmitPending)))
  ) {
    optionsContent = (
      <Alert severity="error">
        We could not load available times for this coaching series. Please try
        again later.
      </Alert>
    );
  } else if (
    (!isFutureSeries && (isOptionsError || nativeOptions.isError)) ||
    options.length === 0
  ) {
    optionsContent = (
      <Alert severity="info">
        No available reschedule times were found for this session.
      </Alert>
    );
  } else {
    optionsContent = (
      <MentoCalDatePicker
        days={days}
        selectedDate={selectedDate}
        comfortable
        onNextMonth={
          visibleMonthIndex < availableMonths.length - 1
            ? () => {
                setVisibleMonth(
                  availableMonths[visibleMonthIndex + 1] ??
                    resolvedVisibleMonth,
                );
                setSelectedDate('');
                setSelectedStartsAt('');
                setIsConfirming(false);
              }
            : undefined
        }
        onPrevMonth={
          visibleMonthIndex > 0
            ? () => {
                setVisibleMonth(
                  availableMonths[visibleMonthIndex - 1] ??
                    resolvedVisibleMonth,
                );
                setSelectedDate('');
                setSelectedStartsAt('');
                setIsConfirming(false);
              }
            : undefined
        }
        onSelectDate={(date) => {
          setSelectedDate(date);
          setSelectedStartsAt('');
          setIsConfirming(false);
        }}
      />
    );
  }

  const canShowSchedulingOptions =
    !isOptionsLoading &&
    (!isFutureSeries ||
      !nativeOptions.isFetching ||
      retainSubmittedPresentation) &&
    (!nativeOptions.isLoading || retainSubmittedPresentation) &&
    !isOptionsError &&
    (!nativeOptions.isError || retainSubmittedPresentation) &&
    !nativeCoachIdentityMissing &&
    (!isFutureSeries || futureSeriesAuthority || isSubmitPending) &&
    options.length > 0;
  const hidePostSubmitMessages = isFutureSeries && submissionStartedAt !== null;
  let rescheduleButtonLabel = isFutureSeries ? 'Update series' : 'Reschedule';
  if (isConfirming) {
    if (lifecycle.restoredSubmitting) {
      rescheduleButtonLabel = APPOINTMENT_RETRY_BUTTON_LABEL;
    } else {
      rescheduleButtonLabel = isFutureSeries
        ? 'Reschedule all future sessions'
        : 'Confirm reschedule';
    }
  }
  if (isFutureSeries && isSubmitPending) rescheduleButtonLabel = 'Updating...';
  // Which view renders is decided once, here, and consumed both by the `content`
  // chain below and by the recovery copy. A separate predicate drifted: the
  // within-48-hours speedbump replaces the whole scheduler, so a restored
  // submission with a selected slot still has no retry button. PR #1649 review.
  const presentation = ((): ReschedulePresentation => {
    if (pendingPresentation) return 'pending';
    if (confirmedSchedule) return 'confirmed';
    if (isPast) return 'past';
    if (showLateRescheduleSpeedbump) return 'speedbump';
    return 'scheduler';
  })();
  // The retry control exists only in the scheduler view, inside `timesContent`,
  // and only once a slot is selected.
  const restoredRetryAvailable =
    presentation === 'scheduler' &&
    Boolean(selectedDate && canShowSchedulingOptions && selectedStartsAt);
  const timesContent =
    selectedDate && canShowSchedulingOptions ? (
      <div
        aria-label="Available times"
        className="flex min-h-0 flex-col gap-4 border-t border-cement-200 p-8 md:h-full md:overflow-hidden md:border-l md:border-t-0"
        role="region"
      >
        <Typography className="font-medium">
          {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            weekday: 'short',
          })}
        </Typography>
        <div className="flex min-h-0 flex-col gap-2 overflow-y-auto md:max-h-80">
          {selectedDateOptions.map((option) => {
            const label = formatSlotTime(option.startsAt);
            const selected = selectedStartsAt === option.startsAt;
            const onSelect = () => {
              setSelectedStartsAt(option.startsAt);
              setIsConfirming(false);
            };
            return isFutureSeries ? (
              <button
                key={option.startsAt}
                aria-label={label}
                aria-pressed={selected}
                className="w-full shrink-0 rounded-lg border border-cement-200 bg-white px-3 py-2 text-sm font-medium aria-pressed:border-sage-500 aria-pressed:bg-sage-500 aria-pressed:text-white"
                type="button"
                onClick={onSelect}
              >
                {label}
              </button>
            ) : (
              <MentoCalTimeSlotButton
                key={option.startsAt}
                label={label}
                selected={selected}
                onClick={onSelect}
              />
            );
          })}
        </div>
        {isConfirming ? (
          <Typography>
            {isFutureSeries
              ? `This updates this session and all following sessions to ${formatSeriesCadence(
                  nativeOptions.data?.activeSeries?.cadenceWeeks ?? 2,
                  selectedStartsAt,
                  nativeOptions.data?.activeSeries?.nextSchedule.timezone ??
                    viewerTimezone,
                )}.`
              : `Confirm rescheduling this session to ${formatSessionTime(selectedStartsAt)}?`}
          </Typography>
        ) : null}
        {isConfirming && nativeAppointmentId ? (
          <TextField
            inputProps={{ maxLength: MEMBER_RESCHEDULE_NOTE_MAX_LENGTH }}
            label={`Note to ${session.coach.firstName} (optional)`}
            minRows={3}
            placeholder="Why are you rescheduling?"
            value={note}
            fullWidth
            multiline
            onChange={(event) => setNote(event.target.value)}
          />
        ) : null}
        {submitError && !hidePostSubmitMessages ? (
          <Alert severity="error">{submitError}</Alert>
        ) : null}
        {lifecycle.reviewed && !hidePostSubmitMessages ? (
          <Alert severity="warning">
            Refreshed appointment state: version {lifecycle.reviewed.version},{' '}
            series {lifecycle.reviewed.seriesId ?? 'standalone'}, fence{' '}
            {lifecycle.reviewed.seriesMutationVersion}.
          </Alert>
        ) : null}
        {lifecycle.reviewUnavailable && !hidePostSubmitMessages ? (
          <Alert severity="warning">
            Authoritative appointment state is unavailable. Nothing was
            submitted; review and reconfirm to try another detail read.
          </Alert>
        ) : null}
        {lifecycle.rejected && !hidePostSubmitMessages ? (
          <Alert severity="warning">
            Rejected operation {lifecycle.rejected.operationId} is retained for
            review.
          </Alert>
        ) : null}
        {pendingOperationId && !hidePostSubmitMessages ? (
          <Alert severity="info">
            {appointmentPendingMessage('reschedule')}
          </Alert>
        ) : null}
        {upgradeRequired && !hidePostSubmitMessages ? (
          <Alert severity="error">
            Appointment client upgrade required. This operation remains saved
            for review; reload or contact support before continuing.
          </Alert>
        ) : null}
        {selectedStartsAt ? (
          <SeriesCoachConflictNote
            conflictingOccurrenceStarts={selectedCoachConflictStarts}
            scope="update"
            timeZone={viewerTimezone}
          />
        ) : null}
        {selectedStartsAt ? (
          <div className="mt-auto flex items-center gap-4 max-md:flex-col-reverse">
            {isConfirming ? (
              <Button variant="text" onClick={() => setIsConfirming(false)}>
                Back
              </Button>
            ) : null}
            <Button
              className="w-full rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500"
              disabled={
                isSubmitting ||
                isNativeSubmitting ||
                isSubmitPending ||
                isRecoveringRetainedCommand ||
                isNativeBaselinePending ||
                nativeRescheduleUnavailable ||
                lifecycle.isReading ||
                baselineDriftDetected ||
                operationReviewRequired ||
                upgradeRequired ||
                Boolean(pendingOperationId)
              }
              variant="contained"
              onClick={
                isConfirming
                  ? () => onConfirm(Boolean(lifecycle.restoredSubmitting))
                  : () => setIsConfirming(true)
              }
            >
              {rescheduleButtonLabel}
            </Button>
          </div>
        ) : null}
      </div>
    ) : null;

  let content: ReactNode;
  if (presentation === 'pending') {
    content = (
      <SessionCardShell className="md:max-w-[482px]">
        <Typography
          className="font-medium"
          id="member-reschedule-session-title"
          variant="h4"
        >
          Pending Appointment
        </Typography>
        <Typography>{appointmentPendingMessage('reschedule')}</Typography>
      </SessionCardShell>
    );
  } else if (presentation === 'confirmed' && confirmedSchedule) {
    content = (
      <SessionCardShell className="md:max-w-[482px]">
        <CoachHeader session={session} />
        <Typography
          className="font-medium"
          id="member-reschedule-session-title"
          variant="h4"
        >
          Your coaching series is updated
        </Typography>
        <DetailRow icon="event">
          {formatSeriesCadence(
            nativeOptions.data?.activeSeries?.cadenceWeeks ?? 2,
            confirmedSchedule.startAt,
            confirmedSchedule.timezone,
          )}
        </DetailRow>
        <Button
          className="self-end rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500"
          variant="contained"
          onClick={() => {
            if (!consumeConfirmedReceipt()) return;
            clearRescheduleMutationIntent(intentKey);
            setPendingIntent(null);
            onRescheduled?.();
            close();
          }}
        >
          Done
        </Button>
      </SessionCardShell>
    );
  } else if (presentation === 'past') {
    content = (
      <SessionCardShell className="md:max-w-[482px]">
        <CoachHeader session={session} />
        <Typography
          className="font-medium"
          id="member-reschedule-session-title"
          variant="h4"
        >
          This session&apos;s time has passed
        </Typography>
        <DetailRow icon="event">
          {formatSessionRange(displayedSession)}
        </DetailRow>
        <DetailRow icon="info_outlined">
          Past sessions can&apos;t be rescheduled or cancelled.
        </DetailRow>
        <Button
          className="self-end rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500"
          variant="contained"
          onClick={requestClose}
        >
          Dismiss
        </Button>
      </SessionCardShell>
    );
  } else if (presentation === 'speedbump') {
    content = (
      <SessionCardShell className="max-md:min-h-[calc(100dvh-86px)] max-md:max-w-none max-md:rounded-none max-md:border-0 max-md:shadow-none md:max-w-[482px]">
        <CoachHeader session={session} />
        <Typography
          className="font-medium"
          id="member-reschedule-session-title"
          variant="h4"
        >
          This session is under 48 hours away
        </Typography>
        <DetailRow icon="event">
          {formatSessionRange(displayedSession)}
        </DetailRow>
        <DetailRow icon="info_outlined">
          <span>
            We know schedules can change.{' '}
            {session.isChemistryCall
              ? 'Your chemistry calls'
              : 'Your coaching calls'}{' '}
            are an important step in the commitment you&apos;ve made to your own
            growth and coaching journey.
            <br />
            <br />
            <strong>
              Whenever possible, please avoid rescheduling within 48 hours
            </strong>{' '}
            as a way of honoring both the commitment you&apos;ve made to
            yourself and the time your coach has dedicated to supporting you.
          </span>
        </DetailRow>
        <div className="flex justify-end gap-4 max-md:mt-auto max-md:flex-col">
          <Button
            variant="text"
            onClick={() => setAcknowledgedSessionKey(acknowledgementKey)}
          >
            Reschedule anyway
          </Button>
          <Button
            className="rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500 max-md:w-full"
            variant="contained"
            onClick={requestClose}
          >
            Keep this time
          </Button>
        </div>
      </SessionCardShell>
    );
  } else {
    content = (
      <Paper
        aria-label="Reschedule session scheduler"
        className={clsx(
          'grid w-full bg-white md:overflow-hidden md:rounded-2xl md:border md:border-cement-200',
          selectedDate
            ? 'md:max-w-[1141px] md:grid-cols-[379px_407px_355px]'
            : 'md:max-w-[786px] md:grid-cols-[379px_407px]',
        )}
        component="section"
        elevation={3}
      >
        <div
          aria-label="Session details"
          className="flex flex-col gap-5 p-8"
          role="region"
        >
          <CoachHeader session={session} />
          <div>
            <Typography
              className="text-2xl font-bold leading-tight"
              id="member-reschedule-session-title"
              variant="h5"
            >
              {isFutureSeries ? 'Update coaching series' : 'Reschedule session'}
            </Typography>
            <Typography className="mt-2 text-cement-400">
              {isFutureSeries
                ? 'Choose a new day and time for this and following sessions.'
                : 'Pick a time that works better for you.'}
            </Typography>
          </div>
          <DetailRow icon="event">
            <span className="flex flex-col">
              <span className="text-sm">
                {isFutureSeries ? 'Current cadence' : 'Former time'}
              </span>
              <span className="line-through">
                {isFutureSeries && nativeOptions.data?.activeSeries
                  ? formatSeriesCadence(
                      nativeOptions.data.activeSeries.cadenceWeeks,
                      nativeOptions.data.activeSeries.nextSchedule.startAt,
                      nativeOptions.data.activeSeries.nextSchedule.timezone,
                    )
                  : formatSessionTime(displayedSession.startedAt)}
              </span>
            </span>
          </DetailRow>
          {memberEmail ? (
            <div className="max-md:hidden">
              <DetailRow icon="person">{memberEmail}</DetailRow>
            </div>
          ) : null}
          <DetailRow icon="schedule">
            {Math.round(
              (new Date(session.endedAt).getTime() -
                new Date(session.startedAt).getTime()) /
                (60 * 1000),
            )}{' '}
            Minutes
          </DetailRow>
          <DetailRow icon="videocam">Google Meet</DetailRow>
          <DetailRow icon="public">
            {shortTimezoneLabel(viewerTimezone, session.startedAt)}
          </DetailRow>
        </div>
        <div
          aria-label="Choose a date"
          className="flex flex-col gap-6 border-t border-cement-200 p-8 md:h-full md:min-h-0 md:overflow-y-auto md:border-l md:border-t-0"
          role="region"
        >
          {optionsContent}
        </div>
        {timesContent}
      </Paper>
    );
  }

  return (
    <MentoCalPageShell
      labelledBy="member-reschedule-session-title"
      presentation="member-reschedule"
      onClose={requestClose}
    >
      {lifecycle.restoredSubmitting && !isFutureSeries ? (
        <Alert severity="info">
          {appointmentRestoredSubmittingMessage('reschedule', {
            retryAvailable: restoredRetryAvailable,
          })}
        </Alert>
      ) : null}
      {content}
    </MentoCalPageShell>
  );
};

export type { MemberRescheduleSessionProps };
